/** @format */

import React, { FC, useState, useEffect, useRef } from 'react'
import { _$, Card, Form, FormItem, Input, Tabs, Tab, Button, Message } from '@king-design/react'
import styled from 'styled-components'
import { MonitorCrumb, ProductGroupSelect, SelectEventRule } from 'src/components'
import { useHistory, useParams } from 'react-router'
import _ from 'lodash'
import AlarmRule, { AlarmRuleHandle } from 'src/containers/MonitorCreate/AlarmRule'
import {
  handleMetricUnits,
  setItemSpacial,
  thanLastPeriod
} from 'src/containers/MonitorCreate/AlarmRule/constants'
import { ProductItem } from 'src/utils/interface'
import { useAsync } from 'react-use'
import { getTemplate, createTemplate, modifyTemplate } from 'src/api/ApiAlarmTemplate'
import { handleParamsCase, capitalizeFirst } from 'src/utils/common'
import useGlobalData from 'src/hooks/useGlobalData'
import { GetListSysEvents } from 'src/api/ApiAlarmServer'
interface Props {
  className?: string
}

const MonitorAlarmTemplateEdit: FC<Props> = ({ className }) => {
  const history = useHistory()
  const params = useParams()
  const editId = _.get(params, 'id')
  // form表单
  const formRef = useRef(null)
  const rulesRef = useRef<AlarmRuleHandle>()
  const [curProduct, setCurProduct] = useState<ProductItem>()
  const [formValues, setFormValues] = useState<any>({
    templateName: '',
    remark: '',
    productType: undefined,
    triggerRules: [],
    eventRules: []
  })
  // 阈值告警-告警分级
  const [alertGrading, setAlertGrading] = useState<boolean>(false)
  const [tabval, setTabval] = useState<'threshold' | 'event'>('threshold')

  const productList = useGlobalData('productList')
  // 初始化获取数据
  useAsync(async () => {
    if (editId) {
      const result = await getTemplate(parseInt(editId))
      const {
        templateName,
        remark,
        productType,
        triggerRules,
        eventRules,
        thresholdReferencedPolicies,
        eventReferencedPolicies
      } = handleParamsCase(result, 'lower')
      setAlertGrading(!!result?.AlertGradingEnabled || false)
      setFormValues({
        templateName,
        remark,
        productType,
        thresholdReferencedPolicies,
        eventReferencedPolicies,
        triggerRules: triggerRules.map(item => {
          const { itemKey, units, triggerValue, displayUnits, hierarchicalValue } = item
          const value = handleMetricUnits(productType, itemKey, triggerValue, units, displayUnits)
          const levelValue = {
            Critical: {
              Value: '',
              Units: units,
              DisplayUnits: displayUnits
            },
            Error: {
              Value: triggerValue,
              Units: units,
              DisplayUnits: displayUnits
            },
            Warning: {
              Value: '',
              Units: units,
              DisplayUnits: displayUnits
            }
          }
          if (hierarchicalValue) {
            Object.entries(hierarchicalValue).forEach(([key, item]) => {
              const { value, units, displayUnits } = item as any
              const trigerValue = handleMetricUnits(
                productType,
                itemKey,
                value,
                units,
                displayUnits
              )
              levelValue[capitalizeFirst(key)] = {
                Value: trigerValue + '',
                Units: units,
                DisplayUnits: displayUnits
              }
            })
          }
          return {
            ...item,
            levelValue,
            triggerValue: value + ''
          }
        }),
        eventRules: eventRules || []
      })
      setCurProduct(findProduct(result?.ProductType))
    }
  }, [editId, productList])

  // 获取产品线数据
  const findProduct = productType => {
    return productList?.find(item => item.productType === parseFloat(productType))
  }

  // 修改表单
  const onFormChange = (values: any) => {
    setFormValues({
      ...formValues,
      ...values
    })
  }

  const { value: eventList } = useAsync(async () => {
    if (formValues?.productType !== undefined) {
      const result = await GetListSysEvents({ ProductType: formValues?.productType })
      if (result && result?.code === 'Success' && result?.data.eventList) {
        return result?.data.eventList
      } else {
        Message.error(result?.message)
        return []
      }
    }
    return []
  }, [formValues?.productType])

  // 提交
  const formSubmit = async () => {
    const validate = await formRef?.current?.validate()
    if (validate) {
      const { templateName, remark, productType, triggerRules, eventRules } = formValues
      if (triggerRules?.length || eventRules?.length) {
        const rulesValid = await rulesRef?.current?.getTriggerRules(alertGrading)
        // 事件只有一个的话 事件名称不能为空
        if (!triggerRules?.length && eventRules?.length === 1 && !eventRules[0].eventName) {
          Message.error(_$('请选择事件'))
          return
        }
        const params: any = {
          TemplateName: templateName,
          Remark: remark || '',
          ProductType: productType,
          AlertGradingEnabled: !!alertGrading,
          EventRules:
            eventRules?.map(item => {
              const rules = handleParamsCase(item)
              delete rules.TagList
              return rules
            }) || []
        }
        if (rulesValid) {
          const rulesParams = rulesValid.map(item => {
            const { itemKey, compare, displayUnits, levelValue } = item
            const res = setItemSpacial(itemKey)
            const upper = handleParamsCase(item)
            return {
              ...upper,
              DisplayUnits: thanLastPeriod.includes(compare) ? '%' : displayUnits,
              Method: upper.Method || 'avg',
              HierarchicalValue: levelValue,
              LevelValue: undefined,
              ...res
            }
          })
          params.TriggerRules = [...rulesParams]
        } else {
          Message.error(_$('请正确填写阈值告警规则'))
          return
        }
        let result = null
        if (editId) {
          params.TemplateID = parseInt(editId)
          result = await modifyTemplate(params)
        } else {
          result = await createTemplate(params)
        }
        if (result) {
          Message.success(editId ? _$('编辑成功') : _$('新建成功'))
          history.push('/monitor/alarmServer/template/home')
        }
      } else {
        Message.error(_$('至少填写一条告警规则'))
        return
      }
    }
  }

  useEffect(() => {
    if (typeof curProduct?.productType === 'number') {
      if (curProduct?.thresholdFlag === 1) {
        setTabval('threshold')
      } else if (curProduct?.sysEventFlag === 1) {
        setTabval('event')
      }
    }
  }, [curProduct])

  return (
    <div className={className}>
      <MonitorCrumb
        breadArr={[
          { title: _$('告警服务') },
          { title: _$('触发条件模版'), route: '/monitor/alarmServer/template/home' },
          { title: editId ? _$('编辑触发条件模版') || '' : _$('新建触发条件模版') }
        ]}
      />
      <Card>
        <>
          <Form starOnRequired ref={formRef} labelWidth={100}>
            <FormItem
              label={_$('模版名称')}
              value={formValues.templateName}
              rules={{ required: true, userName: true }}
              messages={{
                required: _$('请输入模版名称')
              }}
            >
              <Input
                value={formValues.templateName}
                width={500}
                placeholder={_$('请输入模版名称')}
                onChangeValue={value => {
                  onFormChange({ templateName: value })
                }}
              ></Input>
            </FormItem>
            <FormItem label={_$('描述')} value={formValues.remark}>
              <Input
                type="textarea"
                rows="5"
                resize="none"
                value={formValues.remark}
                width={500}
                placeholder={_$('请输入描述')}
                onChangeValue={value => {
                  onFormChange({ remark: value })
                }}
              ></Input>
            </FormItem>
            <FormItem
              label={_$('产品类型')}
              value={formValues?.productType}
              rules={{ required: true }}
              messages={{
                required: _$('请选择产品类型')
              }}
            >
              <ProductGroupSelect
                container=".select_position"
                disabled={!!editId}
                value={formValues?.productType}
                width={500}
                onChange={value => {
                  if (formValues?.productType !== value) {
                    onFormChange({
                      productType: value,
                      triggerRules: []
                    })
                    setCurProduct(findProduct(value))
                  }
                }}
              />
            </FormItem>
            {curProduct?.thresholdFlag === 1 || curProduct?.sysEventFlag === 1 ? (
              <Tabs value={tabval} onChangeValue={tab => setTabval(tab)}>
                {curProduct?.thresholdFlag === 1 ? (
                  <Tab value="threshold">{_$('阈值告警')}</Tab>
                ) : null}
                {curProduct?.sysEventFlag === 1 ? <Tab value="event">{_$('事件告警')}</Tab> : null}
              </Tabs>
            ) : null}
            <AlarmRule
              ref={rulesRef}
              className="alarm_item"
              showAlertGrading={true}
              alertGrading={alertGrading}
              setAlertGrading={setAlertGrading}
              required={formValues?.thresholdReferencedPolicies > 0}
              hidden={curProduct?.thresholdFlag !== 1 || tabval !== 'threshold'}
              productType={formValues?.productType}
              triggerRules={formValues?.triggerRules}
              setTriggerRules={value => {
                onFormChange({
                  triggerRules: value
                })
              }}
            />

            <SelectEventRule
              productType={formValues?.productType}
              hidden={curProduct?.sysEventFlag !== 1 || tabval !== 'event'}
              eventList={eventList}
              eventRules={formValues?.eventRules}
              validate={false}
              onChange={value => {
                onFormChange({
                  eventRules: value
                })
              }}
            />
          </Form>
          <div className="bottom_action">
            <Button
              onClick={() => {
                formSubmit()
              }}
              type="primary"
            >
              {_$('确认')}
            </Button>
            <Button
              onClick={() => {
                history.push('/monitor/alarmServer/template/home')
              }}
              type="default"
            >
              {_$('取消')}
            </Button>
          </div>
        </>
      </Card>
      {/* <EventDialog
            productType={formValues?.productType}
            eventList={eventList}
            defaultRadio={formValues?.eventRadio || undefined}
            defaultValue={formValues?.eventNames || []}
            onChange={(eventRadio, eventNames) => {
              onFormChange({
                eventRadio,
                eventNames
              })
            }}
          /> */}
    </div>
  )
}

export default styled(MonitorAlarmTemplateEdit)`
  padding: 0 16px;
  .k-card {
    position: relative;
    min-height: calc(100vh - 100px);
    padding-bottom: 64px;
    .alarm_rule_title {
      border-top: none;
      .create_model_title {
        font-size: 16px;
      }
    }
    .alarm_item {
      margin-top: 16px;
    }
    .bottom_action {
      display: flex;
      justify-content: flex-end;
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 64px;
      padding: 16px 0;
      background: #ffffff;
      border-top: 1px solid #f0f0f0;
      padding-right: 16px;
      z-index: 1000;
      button {
        font-size: 14px;
        margin-left: 8px;
      }
    }
  }
`
